import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { PrimaryButton, SecondaryButton } from "../../design-system";
import { typography, textColors, interactive } from "../../design-system/tokens";
import { FormField, Input } from "../../design-system/components/inputs";
import AuthLayout from "./AuthLayout";
import { apiFetch } from "../utils/api";

/** Only allow same-origin relative return paths (e.g. /affiliate/login). */
const sanitizeReturnTo = (value) => {
  const raw = String(value || "").trim();
  if (!raw.startsWith("/") || raw.startsWith("//")) return "/edit/login";
  return raw;
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = sanitizeReturnTo(new URLSearchParams(location.search).get("returnTo"));
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [error, setError] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((v) => v - 1), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const requestCode = async () => {
    setError("");
    setStatusMsg("");
    if (!email) {
      setError("Please enter your email.");
      return;
    }
    try {
      const res = await apiFetch("/api/auth/recovery/request/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setStatusMsg("If your email matches an account, a 6-digit code has been sent to it.");
        setResendCooldown(30);
        setCodeSent(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || "Failed to send code");
      }
    } catch (e) {
      setError("Network error. Please try again.");
    }
  };

  const resendCode = async () => {
    if (resendCooldown > 0) return;
    setIsResending(true);
    setError("");
    try {
      const res = await apiFetch("/api/auth/recovery/resend/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setStatusMsg("We resent your 6-digit code.");
        setResendCooldown(30);
      } else if (res.status === 429) {
        setError(data.error || "Please wait before requesting another code.");
      } else {
        setError(data.error || "Failed to resend code.");
      }
    } catch (e) {
      setError("Network error. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const verifyCode = async () => {
    setError("");
    try {
      const res = await apiFetch("/api/auth/recovery/verify/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.recovery_token) {
        navigate("/edit/recovery/password", {
          state: { recoveryToken: data.recovery_token, returnTo },
        });
      } else {
        setError(data.error || "The code is wrong. Please try again.");
      }
    } catch (e) {
      setError("Network error. Please try again.");
    }
  };

  return (
    <AuthLayout
      title="Account recovery"
      subtitle="Enter your email to receive a 6-digit code"
    >
      <h2 className={`${typography.h2} ${textColors.primary} mb-6 text-center`}>Password Recovery</h2>
      {statusMsg && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-md text-center">
          {statusMsg}
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-center">
          {error}
        </div>
      )}
      <div className="space-y-6">
        <FormField 
          label="Email" 
          htmlFor="email"
          required
        >
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </FormField>
        
        <div className="flex items-center gap-3">
          {!codeSent ? (
            <PrimaryButton
              onClick={requestCode}
              variant="gradient"
              className="flex-1"
            >
              Send code
            </PrimaryButton>
          ) : (
            <SecondaryButton
              onClick={resendCode}
              disabled={!email || isResending || resendCooldown > 0}
              className={`flex-1 ${resendCooldown>0?"":"border-blue-600 text-blue-600 hover:bg-blue-50"}`}
            >
              {resendCooldown > 0 ? `Resend (${resendCooldown})` : "Resend"}
            </SecondaryButton>
          )}
        </div>
        
        {codeSent && (
          <>
            <FormField 
              label="6-digit code" 
              htmlFor="code"
              required
            >
              <Input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0,6))}
                placeholder="••••••"
                className="tracking-widest text-center text-lg"
                maxLength={6}
              />
            </FormField>
            
            <PrimaryButton
              onClick={verifyCode}
              variant="gradient"
              fullWidth={true}
            >
              Verify code
            </PrimaryButton>
          </>
        )}
      </div>
      
      <div className="mt-6 text-center">
        <p className={`${typography.body} ${textColors.secondary}`}>
          Remember your password?{" "}
          <Link
            to={returnTo}
            className={`underline font-medium transition-colors ${interactive.link} ${interactive.linkHover}`}
          >
            Sign in
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;

